import { useEffect, useRef, useState } from "react";
import { IpCell } from "@/components/IpCell";
import { ReplayButton } from "@/components/ReplayButton";

type Event = {
  ts: string;
  method: string;
  path: string;
  status: number | null;
  latency_ms: number | null;
  bytes_down: number | null;
  source_ip: string | null;
  error: string | null;
};

const BUFFER = 100;

export function LiveTail({ proxyKeyId }: { proxyKeyId: number }) {
  const [events, setEvents] = useState<Event[]>([]);
  const [paused, setPaused] = useState(false);
  const [connected, setConnected] = useState(false);
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (paused) return;
    const es = new EventSource(`/api/admin/proxy-keys/${proxyKeyId}/tail`, {
      withCredentials: true,
    });
    esRef.current = es;
    es.addEventListener("ready", () => setConnected(true));
    es.onmessage = (e) => {
      try {
        const ev: Event = JSON.parse(e.data);
        setEvents((prev) => [ev, ...prev].slice(0, BUFFER));
      } catch {
        /* ignore non-JSON frames */
      }
    };
    es.onerror = () => {
      setConnected(false);
      // EventSource auto-reconnects; leave it alone
    };
    return () => {
      es.close();
      esRef.current = null;
      setConnected(false);
    };
  }, [proxyKeyId, paused]);

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold">Live tail</h2>
        <div className="flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1.5 text-slate-400">
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                paused
                  ? "bg-slate-500"
                  : connected
                  ? "bg-emerald-500 animate-pulse"
                  : "bg-rose-500"
              }`}
            />
            {paused ? "paused" : connected ? "live" : "connecting"}
          </span>
          <button
            className="btn-secondary text-xs py-1"
            onClick={() => setPaused((v) => !v)}
          >
            {paused ? "Resume" : "Pause"}
          </button>
          <button
            className="btn-secondary text-xs py-1"
            onClick={() => setEvents([])}
            disabled={events.length === 0}
          >
            Clear
          </button>
        </div>
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-slate-500">
          {paused
            ? "Paused. Resume to see new requests."
            : "Waiting for traffic… this pane updates in real time the moment this proxy key is used."}
        </p>
      ) : (
        <ul className="space-y-1 mono text-xs max-h-96 overflow-auto">
          {events.map((e, i) => {
            const tone =
              e.error || (e.status && e.status >= 500)
                ? "text-rose-300"
                : e.status && e.status >= 400
                ? "text-amber-300"
                : "text-slate-300";
            return (
              <li
                key={`${e.ts}-${i}`}
                className="grid grid-cols-[auto_auto_1fr_auto_auto_auto_auto] gap-3 items-baseline border-b border-white/5 pb-1"
              >
                <span className="text-slate-500">
                  {new Date(e.ts).toLocaleTimeString()}
                </span>
                <span className="text-slate-400">{e.method}</span>
                <span className={`truncate ${tone}`}>
                  {e.path}
                  {e.error ? ` — ${e.error}` : ""}
                </span>
                <span className={tone}>{e.status ?? "—"}</span>
                <span className="text-slate-500">{e.latency_ms ?? 0}ms</span>
                <IpCell ip={e.source_ip} />
                <ReplayButton method={e.method} path={e.path} label="↻" />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
